import { BlogDetailsPage, ChatBot, NextPageHead, PageView } from '@aspendental/shared-components-web';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import DetermineIndexing from '../utils/determine-indexing';
import { getDeviceTypeFromHeaders } from '../utils/get-device-type-from-headers';

import {
	AppMetadataByAppNameDocument,
	AppPagesDataByAppNameDocument,
	BlogDetailsPageByPathDocument,
	BlogDetailsPageFragment,
	Page as IPage,
} from '@src/lib/__generated/sdk';
import { getClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import choosePreview from '@src/pages/utils/choose-preview';
import convertPathToBreadCrumbTitle from '@src/pages/utils/convert-path-to-breadcrumb-title';
import getConfigByBrand from '@src/pages/utils/get-config-by-brand';
import getConfigServices from '@src/pages/utils/get-config-services';
import { getServerSideTranslations } from '@src/pages/utils/get-serverside-translations';
import getThemeByDomainName from '@src/pages/utils/get-theme-by-domain-name';

const Page = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
	const { siteURL, features, featureFlags, logo } = props.config as BrandThemeConfig;
	const { url: logoUrl } = props.appData?.logo || {};
	const { pageData, path, breadcrumbsData, preview } = props;
	const { metaTitle, metaDescription, canonical, indexed, analyticsContext, ogImage } = pageData;

	return (
		<>
			<PageView analyticsContext={analyticsContext} siteSection="Marketing"/>
			<NextPageHead
				path={path}
				title={metaTitle}
				indexed={DetermineIndexing(indexed, props.env, preview, featureFlags.doNotIndexWholeSite)}
				description={metaDescription}
				canonical={canonical}
				originUrl={siteURL}
				breadcrumbTitle={breadcrumbsData.breadcrumbTitle}
				breadcrumbs={breadcrumbsData.breadcrumbsCollection.items}
				ogImage={ogImage?.desktopImage?.image?.cdn_url ?? logo.dark}
			/>
			<BlogDetailsPage {...pageData} />
			{features.chat.enabled && (
				<ChatBot
					url={features.chat.chatBotURL}
					isIconButton={features.chat.isIconButton}
					logoUrl={logoUrl}
					featureList={featureFlags.featureList}
				/>
			)}
		</>
	);
};

export const getServerSideProps: GetServerSideProps = async ({ params, locale, req, preview = false }) => {
	if (!params?.slug) {
		return {
			notFound: true,
		};
	}
	const domain = req.headers.host;
	const deviceType = getDeviceTypeFromHeaders(req.headers);
	const theme = getThemeByDomainName(domain);
	const headerTagTheme = Array.isArray(req.headers['x-tag-theme'])
		? (req.headers['x-tag-theme'] as TagThemeType[])[0]
		: (req.headers['x-tag-theme'] as TagThemeType);
	// Priority: HTTP header x-tag-theme > domain > process.env.TAG_BRAND_THEME
	const tagTheme = headerTagTheme || theme || process.env.TAG_BRAND_THEME || 'aspendental';
	const logger = getLogger('blog-detail-page', tagTheme);
	try {
		const brandConfig = getConfigByBrand(tagTheme);
		preview = choosePreview(req.headers.host, preview);
		const appName = brandConfig.contentfulAppId || process.env.TAG_APP_NAME || 'TAG_Commercial_Web_Sandbox';
		const gqlClient = getClient(preview);
		if (gqlClient === null) {
			logger.error('GQL_CLIENT_IS_NULL: missing token for fetching contentful data');
			throw new Error('gqlClient is null');
		}

		const [appPagesData, appMetadata] = await Promise.all([
			gqlClient.query(AppPagesDataByAppNameDocument, { appName, preview }).toPromise(),
			gqlClient.query(AppMetadataByAppNameDocument, { appName, preview }).toPromise(),
		]);
		const appData = {
			...appPagesData.data?.appCollection?.items[0],
			...appMetadata.data?.appCollection?.items[0],
		};

		// replace [env] with environment name for locationBarFindOfficeUrl
		// TODO: create a brand variable context to replace those variables from CMS in future
		if (appData && appData.header?.locationBarFindOfficeUrl) {
			const brandSubdomain = process.env.ENVIRONMENT_NAME !== 'prod' ? 'wwwstg' : 'www';
			appData.header.locationBarFindOfficeUrl = appData.header?.locationBarFindOfficeUrl?.replace(
				'[env]',
				brandSubdomain
			);
		}

		if (!appData) {
			return {
				notFound: true,
			};
		}

		const slugs = params.slug as string[];

		// this path value needed for page indexing
		const path = '/' + slugs.join('/');
		logger.info(path);

		//only the last section in the url path is the slug to query
		const pathForQuery = slugs[slugs.length - 1];
		const title = brandConfig.name;
		const pageData = await gqlClient
			.query(BlogDetailsPageByPathDocument, { path: pathForQuery, title, preview })
			.toPromise();
		if (pageData.error) {
			logger.error(`CONTENTFUL_ERROR_BLOG_DETAILS_PAGE: ${pageData.error}`);
		}
		const page = pageData.data.blogDetailsPageCollection?.items[0] as BlogDetailsPageFragment;

		if (!page) {
			logger.error(`COULD_NOT_FIND_THE_BLOG_SLUG: ${pathForQuery}`);
			return {
				notFound: true,
			};
		}

		// breadcrumb data generation for blog detail page
		const {
			features: {
				blogs: { root },
			},
		} = brandConfig;
		const breadcrumbsData = {
			// current page title from the slug value
			breadcrumbTitle: convertPathToBreadCrumbTitle(slugs[slugs.length - 1]),
			// current page path
			path,
			// just blog list page in the hierarchy now
			// need to add category pages once that is built
			breadcrumbsCollection: {
				items: [
					{
						breadcrumbTitle: 'Home',
						path: '/',
					} as IPage,
					{
						breadcrumbTitle: convertPathToBreadCrumbTitle(root),
						path: root,
					} as IPage,
				],
			},
		};

		let hasIpBasedCoordinates = false;
		let coordinates = {
			latitude: 0,
			longitude: 0,
		};

		if (req.headers['cloudfront-viewer-latitude'] && req.headers['cloudfront-viewer-longitude']) {
			hasIpBasedCoordinates = true;
			coordinates = {
				latitude: parseFloat(req.headers['cloudfront-viewer-latitude'] as string),
				longitude: parseFloat(req.headers['cloudfront-viewer-longitude'] as string),
			};
			logger.info(`CDN_GEO_LOCATION_FOUND: [${coordinates?.latitude}, ${coordinates?.longitude}]`);
		} else {
			logger.info(`CDN_GEO_LOCATION_MISS`);
		}

		const transformedServices = getConfigServices(req, brandConfig);

		return {
			props: {
				...(await getServerSideTranslations(locale)),
				env: {
					ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME,
					DENTRINO_CLIENT_ID: process.env.DENTRINO_CLIENT_ID,
					DENTRINO_CLIENT_SECRET: process.env.DENTRINO_CLIENT_SECRET,
				},
				config: {
					...brandConfig,
					...(appData.featureFlags ? { featureFlags: appData.featureFlags } : {}),
					services: transformedServices,
					schemaUrl: `${brandConfig.siteURL}${req.url}`,
				},
				pageData: page,
				user: {
					coordinates,
					hasIpBasedCoordinates,
				},
				appData,
				footerData: appData?.footer,
				path,
				breadcrumbsData,
				preview,
				deviceType,
			},
		};
	} catch (e) {
		logger.error(`BLOG_DETAIL_PAGE_LOAD_FAILURE: ${e}`);
		return {
			notFound: true,
		};
	}
};

export default Page;
